import { Chip, Grid } from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import { RELATION_OWNED_BY } from '@backstage/catalog-model';
import {
  HeaderIconLinkRow,
  InfoCard,
  MarkdownContent,
} from '@backstage/core-components';
import { AboutField } from '@backstage/plugin-catalog';
import {
  EntityRefLinks,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { DeployStateChip } from '@internal/backstage-plugin-sql-pipeline';
import { ANNOTATION_DAG_REPO } from '@internal/backstage-plugin-sql-pipeline-common';

/** 파이프라인 annotation 이 없던 엔티티의 기본값(= 현재 운영 중인 DAG 저장소). */
const DEFAULT_DAG_REPO = 'yogiyo/dp-lakehouse';

/**
 * 파이프라인 엔티티의 About 카드.
 *
 * 기본 `EntityAboutCard` 는 Component 면 `spec.lifecycle` 을 항상 그린다. 그 값은
 * 생성 시 `production` 으로 고정이라 개발계에만 있는 파이프라인도 운영 중으로
 * 읽혔고, 항목을 빼는 옵션이 없다. 그래서 같은 항목을 두고 Lifecycle 자리에
 * 반영 상태(Data Hub 목록과 같은 값)를 둔다.
 *
 * 링크는 DAG 저장소의 Pull requests 화면이다. 파이프라인을 만들고 고치면 그곳에
 * PR 이 생기고 사용자가 그것을 머지한다 — 기본 카드의 View Source(DAG 폴더)보다
 * 자주 간다.
 *
 * 기본 카드의 편집(연필) 버튼도 두지 않는다 — catalog-info.yaml 은 Backstage 가
 * 생성·관리하는 파일이라 손으로 고치면 다음 저장에서 덮어써진다.
 */
export const PipelineAboutCard = () => {
  const { entity } = useEntity();
  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  const dagRepo =
    entity.metadata.annotations?.[ANNOTATION_DAG_REPO] ?? DEFAULT_DAG_REPO;
  const tags = entity.metadata.tags ?? [];

  return (
    <InfoCard
      title="About"
      subheader={
        <HeaderIconLinkRow
          links={[
            {
              label: 'View Airflow GitHub',
              icon: <GitHubIcon />,
              href: `https://github.com/${dagRepo}/pulls`,
            },
          ]}
        />
      }
    >
      <Grid container>
        <Grid item xs={12}>
          <AboutField label="Description">
            <MarkdownContent
              content={entity.metadata.description ?? 'No description'}
            />
          </AboutField>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Owner">
            <EntityRefLinks entityRefs={owners} defaultKind="group" />
          </AboutField>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Type" value={(entity.spec as any)?.type} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Tags">
            {tags.map(t => (
              <Chip key={t} size="small" label={t} />
            ))}
          </AboutField>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="반영 상태">
            <DeployStateChip pipelineName={entity.metadata.name} />
          </AboutField>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
